import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Camera, Clock, CheckCircle, UserCheck, MapPin, Loader2, LogIn, LogOut } from 'lucide-react';
import FaceCapture from './FaceCapture';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Audio context for alert sound
const playAlertSound = (type = 'in') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Different tones for punch in vs punch out
    if (type === 'in') {
      // Pleasant ascending tone for punch in
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
    } else {
      // Descending tone for punch out
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime); // G5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime + 0.2); // C5
    }
    
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.4);
  } catch (error) {
    console.warn('Could not play alert sound:', error);
  }
};

const AttendancePunch = ({ franchiseId, isOpen, onClose }) => {
  const [staffDescriptors, setStaffDescriptors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [identifiedStaff, setIdentifiedStaff] = useState(null);
  const [punchType, setPunchType] = useState('in');
  const [location, setLocation] = useState(null);
  const [punchResult, setPunchResult] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [currentDescriptor, setCurrentDescriptor] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchStaffDescriptors();
      getLocation();
      setPunchResult(null);
      setIdentifiedStaff(null);
      // Auto-open camera when modal opens
      setShowCamera(true);
    }
  }, [isOpen, franchiseId]);

  const fetchStaffDescriptors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/staff?franchise_id=${franchiseId}`);
      const staff = response.data.staff || [];
      
      // Filter only staff with face descriptors
      const descriptors = staff
        .filter(s => s.face_descriptor && s.is_active !== false)
        .map(s => ({
          staff_id: s.staff_id,
          full_name: s.full_name,
          face_descriptor: s.face_descriptor
        }));
      
      setStaffDescriptors(descriptors);
      setLoading(false);
      
      if (descriptors.length === 0) {
        toast.warning('No staff with registered faces found');
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setLoading(false);
      toast.error('Failed to load staff data');
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Location error:', error);
          // Continue without location
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  const handleStaffIdentified = async (staff, descriptor) => {
    setIdentifiedStaff(staff);
    setCurrentDescriptor(descriptor);
    
    // Determine next punch type
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/attendance?franchise_id=${franchiseId}&staff_id=${staff.staff_id}&start_date=${today}&end_date=${today}`
      );
      const todayPunches = response.data.attendance || [];
      const nextPunch = todayPunches.length % 2 === 0 ? 'in' : 'out';
      setPunchType(nextPunch);
    } catch (error) {
      console.error('Error checking attendance:', error);
    }
  };

  const handleFaceCapture = async (data) => {
    // Prevent multiple calls
    if (punching || punchResult?.success) {
      return;
    }
    
    if (!data.identifiedStaff) {
      toast.error('Face not recognized. Please try again or register first.');
      setShowCamera(false);
      return;
    }

    setPunching(true);
    try {
      const response = await axios.post(`${API}/attendance/punch`, {
        franchise_id: franchiseId,
        staff_id: data.identifiedStaff.staff_id,
        punch_type: punchType,
        face_descriptor: data.descriptor,
        latitude: location?.latitude,
        longitude: location?.longitude,
        selfie_image: data.image
      });

      // Play alert sound based on punch type
      playAlertSound(response.data.punch_type);

      setPunchResult({
        success: true,
        staffName: response.data.staff_name,
        punchType: response.data.punch_type,
        punchTime: response.data.punch_time,
        matchScore: response.data.face_match_score
      });

      toast.success(`${response.data.punch_type.toUpperCase()} recorded for ${response.data.staff_name}`);
      setShowCamera(false);
    } catch (error) {
      console.error('Error recording punch:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to record attendance';
      toast.error(errorMsg);
      setPunchResult({ success: false, error: errorMsg });
    } finally {
      setPunching(false);
    }
  };

  const handleClose = () => {
    setPunchResult(null);
    setIdentifiedStaff(null);
    setShowCamera(false);
    onClose();
  };

  const startCamera = () => {
    setShowCamera(true);
    setPunchResult(null);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-surface max-w-md p-0 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <p className="text-secondary">Loading staff data...</p>
          </div>
        ) : punchResult?.success ? (
          // Success Screen - Green for IN, Red for OUT
          <div className={`flex flex-col items-center justify-center p-8 ${
            punchResult.punchType === 'in' ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
              punchResult.punchType === 'in' ? 'bg-green-500' : 'bg-red-500'
            }`}>
              {punchResult.punchType === 'in' ? (
                <LogIn className="h-12 w-12 text-white" />
              ) : (
                <LogOut className="h-12 w-12 text-white" />
              )}
            </div>
            <h2 className={`text-2xl font-bold mb-2 ${
              punchResult.punchType === 'in' ? 'text-green-700' : 'text-red-700'
            }`}>
              Punch {punchResult.punchType.toUpperCase()} Successful!
            </h2>
            <p className="text-lg font-medium text-gray-700 mb-1">{punchResult.staffName}</p>
            <p className={`text-3xl font-bold mb-2 ${
              punchResult.punchType === 'in' ? 'text-green-600' : 'text-red-600'
            }`}>{punchResult.punchTime}</p>
            <p className="text-sm text-gray-500 mb-4">Match Score: {punchResult.matchScore}%</p>
            
            {location && (
              <p className="text-xs text-gray-400 flex items-center gap-1 mb-4">
                <MapPin className="h-3 w-3" />
                Location recorded
              </p>
            )}
            
            <Button onClick={handleClose} className={`text-white ${
              punchResult.punchType === 'in' 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'
            }`}>
              Done
            </Button>
          </div>
        ) : showCamera ? (
          // Camera View
          <FaceCapture
            mode="attendance"
            staffDescriptors={staffDescriptors}
            onCapture={handleFaceCapture}
            onCancel={() => setShowCamera(false)}
            onStaffIdentified={handleStaffIdentified}
          />
        ) : (
          // Initial Screen
          <div className="p-6">
            <DialogHeader className="mb-6">
              <DialogTitle className="flex items-center gap-2 text-xl">
                <UserCheck className="h-6 w-6" />
                Staff Attendance
              </DialogTitle>
            </DialogHeader>

            <div className="text-center mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Camera className="h-12 w-12 text-primary" />
              </div>
              <p className="text-secondary mb-2">
                Take a selfie to mark your attendance
              </p>
              <p className="text-xs text-gray-400">
                {staffDescriptors.length} staff member(s) registered
              </p>
            </div>

            {/* Current Time */}
            <div className="bg-muted rounded-lg p-4 mb-6 text-center">
              <p className="text-sm text-secondary mb-1">Current Time</p>
              <p className="text-3xl font-bold text-primary">
                {new Date().toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })}
              </p>
              <p className="text-sm text-secondary mt-1">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {location && (
              <p className="text-xs text-center text-green-600 flex items-center justify-center gap-1 mb-4">
                <MapPin className="h-3 w-3" />
                Location enabled
              </p>
            )}

            {punchResult?.error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm text-center">
                {punchResult.error}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={startCamera}
                disabled={staffDescriptors.length === 0 || punching}
                className="flex-1 bg-primary text-white"
              >
                {punching ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4 mr-2" />
                    Take Selfie
                  </>
                )}
              </Button>
            </div>

            {staffDescriptors.length === 0 && (
              <p className="text-xs text-center text-red-500 mt-3">
                No staff registered with face. Please register staff first.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttendancePunch;
